import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChild,
  TemplateRef,
  OnInit,
  ElementRef,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { PaginatorModule } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import {
  formatCurrency,
  formatNumber,
} from '@shared/utils/date-formatter.util';
export interface TableColumn {
  label: string;
  prop: string;
  disabled?: boolean;
  width?: number;
  type?:
    | 'text'
    | 'status'
    | 'actions'
    | 'custom'
    | 'select'
    | 'date'
    | 'currency'
    | 'number'
    | 'inputNumber'
    | 'inputNumberHorizontal'
    | 'inputText'
    | 'inputDate';
  template?: string;
  styleClass?: string;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  useGrouping?: boolean;
  options?: { label: string; value: any }[];
  min?: number;
  max?: number | string;
  step?: number;
  placeholder?: string;
  /** Khi set: dùng rowData[placeholderProp] làm placeholder (theo từng dòng). */
  placeholderProp?: string;
  required?: boolean;
  sortable?: boolean;
  sortField?: string;
  showButtons?: boolean;
  filter?: boolean;
  headerTooltip?: string; // Tooltip text for column header
}

export interface PaginationEvent {
  first?: number;
  rows?: number;
  page?: number;
  pageCount?: number;
}

export interface SortEvent {
  field: string;
  order: 1 | -1; // 1 for ascending, -1 for descending
}

@Component({
  selector: 'app-data-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    PaginatorModule,
    CheckboxModule,
    TooltipModule,
    TranslateModule,
    ButtonModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    DatePickerModule,
  ],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush, // Using OnPush for better performance
})
export class DataTableComponent implements OnInit, AfterViewInit, OnChanges {
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  @Input() dataSource: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() rows = 20;
  @Input() first = 0;
  @Input() totalRecords = 0;
  @Input() scrollHeight = '65vh';
  @Input() tableStyle: { [key: string]: any } = {
    'min-width': '50rem',
    width: '100%',
  };
  @Input() goToPageNumber = 1;
  @Input() loading = false;
  @Input() rowsPerPageOptions: number[] = [20, 50, 100];
  @Input() selection: any[] = [];
  @Input() hasCheckColumn: boolean = false;
  @Input() hasHeaderCheckbox: boolean = true;
  @Input() hasSerialColumn: boolean = false;
  /** Name of an optional nested collection rendered below an expandable row. */
  @Input() expansionChildrenProp = 'packageChildren';
  /** Expand newly loaded nested rows without reopening rows the user collapsed. */
  @Input() autoExpandRowsWithChildren = false;
  @Input() isRowDisabledFn?: (rowData: any) => boolean; // Function to check if row is disabled
  @Input() showPaginator: boolean = true;
  @Input() isRowSelectedFn?: (rowData: any) => boolean; // Function to check if row is selected (for disabled items)
  @Input() actionsTemplate?: TemplateRef<any>;
  @Input() getFieldError?: (
    rowIndex: number,
    fieldName: string,
  ) => string | null;
  @Input() sortField?: string;
  @Input() sortOrder?: 1 | -1;

  private previousDataSource: any[] = [];

  // Compute paged data for client-side pagination when external paging is not wired
  get pagedData(): any[] {
    const data = Array.isArray(this.dataSource) ? this.dataSource : [];
    const start = Math.max(0, this.first || 0);
    const rows = Math.max(0, this.rows || data.length);
    const end = Math.min(start + rows, data.length);
    return data.slice(start, end);
  }

  @ContentChild('statusTemplate') statusTemplate?: TemplateRef<any>;
  @ContentChild('actionsTemplate') contentActionsTemplate?: TemplateRef<any>;
  @ContentChild('customTemplate') customTemplate?: TemplateRef<any>;

  @Output() selectionChange = new EventEmitter<any[]>();
  @Output() selectionAll = new EventEmitter<void>();
  @Output() pageChange = new EventEmitter<PaginationEvent>();
  @Output() goToPage = new EventEmitter<void>();
  @Output() cellValueChange = new EventEmitter<{
    rowData: any;
    prop: string;
    value: any;
  }>();
  @Output() cellInputChange = new EventEmitter<{
    rowData: any;
    prop: string;
    value: any;
  }>();
  @Output() sortChange = new EventEmitter<SortEvent>();

  isAllSelected = false;
  isLoading = false;
  private readonly expandedRowIds = new Set<string | number>();
  private readonly autoExpandedRowIds = new Set<string | number>();

  @ViewChild('tableContainer') tableContainer!: ElementRef;

  // Internal no data message properties

  get noDataDescription(): string {
    return 'common.table.no_data_message';
  }

  get maxPage(): number {
    return Math.ceil(this.totalRecords / this.rows) || 1;
  }

  ngOnInit(): void {
    this.isLoading = this.loading;
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check if data source has changed
    if (changes['dataSource'] && this.dataSource !== this.previousDataSource) {
      this.previousDataSource = [...this.dataSource];
      this.expandNewRowsWithChildren();
      // Force change detection for OnPush strategy
      this.cdr.markForCheck();
      // Setup tooltips after data change
      setTimeout(() => {
        this.setupTooltips();
      }, 100);
    }

    if (changes['autoExpandRowsWithChildren']) {
      this.expandNewRowsWithChildren();
    }

    // Handle selection changes
    if (changes['selection']) {
      // Update select all state when selection input changes
      this.updateSelectAllState();
      this.cdr.markForCheck();
    }

    // Mark for check on any input change for OnPush strategy
    if (
      changes['rows'] ||
      changes['first'] ||
      changes['totalRecords'] ||
      changes['loading']
    ) {
      this.cdr.markForCheck();
      this.setupPaginatorTestIds();
    }
  }

  ngAfterViewInit(): void {
    this.setupTooltips();
    this.setupPaginatorTestIds();
  }

  private setupTooltips(): void {
    // Setup tooltips for ellipsed text after view is initialized
    setTimeout(() => {
      this.addTooltipsToEllipsedCells();
    }, 100);
  }

  private addTooltipsToEllipsedCells(): void {
    if (!this.tableContainer) return;

    const cells =
      this.tableContainer.nativeElement.querySelectorAll('.truncate-cell');
    cells.forEach((cell: HTMLElement) => {
      // Only add tooltip if text is actually ellipsed
      if (this.isTextEllipsed(cell)) {
        const text = cell.textContent?.trim() || '';
        if (text) {
          cell.setAttribute('pTooltip', text);
          cell.setAttribute('tooltipPosition', 'top');
          cell.setAttribute('tooltipStyleClass', 'custom-tooltip');
        }
      }
    });
  }

  private isTextEllipsed(element: HTMLElement): boolean {
    return element.scrollWidth > element.clientWidth;
  }

  // Method to check if a cell should show tooltip based on content and column type
  shouldShowTooltip(text: any, column: TableColumn): boolean {
    if (
      !text ||
      column.type === 'actions' ||
      column.type === 'status' ||
      column.type === 'custom' ||
      column.type === 'date' ||
      column.prop === 'durationDisplay' // Exclude duration column from tooltips
    ) {
      return false;
    }

    // For text columns, show tooltip if content is longer than 20 characters
    if (typeof text === 'string' && text.length > 20) {
      return true;
    }

    return false;
  }

  getColumnWidth(column: TableColumn): number {
    // Actions columns - use specified width if provided, otherwise default to 180px
    // Allow smaller widths when explicitly set (e.g., for result tables with single action)
    if (column.type === 'actions') {
      return column.width || 180; // Use specified width or default to 180px
    }

    // Status columns need adequate width for status text
    if (column.type === 'status') {
      return Math.max(column.width || 0, 120); // Minimum 120px for status
    }

    // Default width or custom width from column definition
    return column.width || 120;
  }

  hasExpandableChildren(rowData: any): boolean {
    return (
      Array.isArray(rowData?.[this.expansionChildrenProp]) &&
      rowData[this.expansionChildrenProp].length > 0
    );
  }

  private expandNewRowsWithChildren(): void {
    if (!this.autoExpandRowsWithChildren) {
      return;
    }

    for (const row of this.dataSource || []) {
      if (!this.hasExpandableChildren(row)) {
        continue;
      }

      const rowId = this.getRowExpansionId(row);
      if (this.autoExpandedRowIds.has(rowId)) {
        continue;
      }

      this.autoExpandedRowIds.add(rowId);
      this.expandedRowIds.add(rowId);
    }
  }

  getExpansionChildren(rowData: any): any[] {
    return this.hasExpandableChildren(rowData)
      ? rowData[this.expansionChildrenProp]
      : [];
  }

  getPackageChildColumnValue(child: any, column: TableColumn): string | number {
    switch (column.prop) {
      case 'spuName':
      case 'spuCode':
      case 'skuName':
      case 'skuCode':
        return child[column.prop] || '-';
      case 'quantity':
        return child.quantity ?? '-';
      default:
        return '-';
    }
  }

  isRowExpanded(rowData: any): boolean {
    return this.expandedRowIds.has(this.getRowExpansionId(rowData));
  }

  toggleRowExpansion(event: Event, rowData: any): void {
    event.stopPropagation();
    const rowId = this.getRowExpansionId(rowData);
    if (this.expandedRowIds.has(rowId)) {
      this.expandedRowIds.delete(rowId);
    } else {
      this.expandedRowIds.add(rowId);
    }
    this.cdr.markForCheck();
  }

  getTableColumnCount(): number {
    return (
      this.columns.length +
      (this.hasCheckColumn ? 1 : 0) +
      (this.hasSerialColumn ? 1 : 0)
    );
  }

  getSerialColumnWidth(): number {
    return this.dataSource.some((row) => this.hasExpandableChildren(row))
      ? 48
      : 40;
  }

  private getRowExpansionId(rowData: any): string | number {
    return rowData?.id ?? rowData?.skuCode;
  }

  onPageChange(event: PaginationEvent) {
    // Update internal state so the table slices data accordingly
    if (typeof event.first === 'number') this.first = event.first;
    if (typeof event.rows === 'number') this.rows = event.rows;
    if (typeof event.page === 'number')
      this.goToPageNumber = (event.page || 0) + 1;
    this.pageChange.emit(event);
    this.setupPaginatorTestIds();
  }

  private setupPaginatorTestIds(): void {
    setTimeout(() => {
      const host = this.elementRef.nativeElement as HTMLElement;
      const paginator = host.querySelector('p-paginator') as HTMLElement | null;
      if (!paginator) return;

      const buttonTestIds: Record<string, string> = {
        '.p-paginator-first': 'data-table-paginator-first-page',
        '.p-paginator-prev': 'data-table-paginator-previous-page',
        '.p-paginator-next': 'data-table-paginator-next-page',
        '.p-paginator-last': 'data-table-paginator-last-page',
      };

      Object.entries(buttonTestIds).forEach(([selector, testId]) => {
        paginator
          .querySelector(selector)
          ?.setAttribute('data-testid', testId);
      });

      Array.from(
        paginator.querySelectorAll(
          '.p-paginator-page',
        ) as NodeListOf<HTMLElement>,
      ).forEach((button: HTMLElement, index: number) => {
        button.setAttribute(
          'data-testid',
          `data-table-paginator-n-${index + 1}`,
        );
      });
    }, 0);
  }

  onGoToPage() {
    // Validate the page number
    if (this.goToPageNumber < 1) {
      this.goToPageNumber = 1;
    } else if (this.goToPageNumber > this.maxPage) {
      this.goToPageNumber = this.maxPage;
    }

    // Calculate the new first index (0-based)
    const newFirst = (this.goToPageNumber - 1) * this.rows;

    // Emit page change event in the same format as PrimeNG paginator
    // PrimeNG emits: { first: number, rows: number, page: number, pageCount: number }
    // where page is 0-based
    this.pageChange.emit({
      first: newFirst,
      rows: this.rows,
      page: this.goToPageNumber - 1, // 0-based page (same as PrimeNG)
      pageCount: this.maxPage,
    });
  }

  onSort(column: TableColumn) {
    if (!column.sortable) return;

    let newOrder: 1 | -1 = 1; // Default to ascending

    // If this column is already being sorted, toggle the order
    if (this.sortField === column.prop) {
      newOrder = this.sortOrder === 1 ? -1 : 1;
    }

    this.sortChange.emit({
      field: column.prop,
      order: newOrder,
    });
  }

  onSelectionChange(event: any, rowData: any) {
    // Prevent selection of disabled rows
    if (this.isRowDisabled(rowData)) {
      return;
    }

    if (event.checked) {
      // Add row to selection - use ID comparison for reliability
      const isAlreadySelected = rowData?.id
        ? this.selection.some((item) => item && item.id === rowData.id)
        : this.selection.includes(rowData);

      if (!isAlreadySelected) {
        this.selection = [...this.selection, rowData];
      }
    } else if (rowData?.id) {
      // Remove row from selection - use ID comparison for reliability
      this.selection = this.selection.filter(
        (item) => !item || item.id !== rowData.id,
      );
    } else {
      this.selection = this.selection.filter((item) => item !== rowData);
    }

    // Update select all state
    this.updateSelectAllState();

    this.selectionChange.emit(this.selection);
  }

  onSelectionAll() {
    this.isAllSelected = !this.isAllSelected;

    if (this.isAllSelected) {
      // Select only non-disabled rows
      this.selection = this.dataSource.filter(
        (row) => !this.isRowDisabled(row),
      );
    } else {
      // Deselect all rows
      this.selection = [];
    }

    // Update select all state (this will be consistent with the new state)
    this.updateSelectAllState();

    this.selectionChange.emit(this.selection);
    this.selectionAll.emit();
  }

  isRowSelected(rowData: any): boolean {
    // Use custom selected function if provided (for disabled items that should appear selected)
    if (this.isRowSelectedFn) {
      return this.isRowSelectedFn(rowData);
    }

    // Use ID comparison for more reliable matching
    if (rowData?.id) {
      return this.selection.some((item) => item && item.id === rowData.id);
    }
    // Fallback to object reference comparison
    return this.selection.includes(rowData);
  }

  isRowDisabled(rowData: any): boolean {
    // Use custom disabled function if provided
    if (this.isRowDisabledFn) {
      return this.isRowDisabledFn(rowData);
    }

    // Check if the row has a disable field (1 = disabled, 0 = enabled)
    if (rowData.disable !== undefined) {
      return rowData.disable === 1;
    }

    // Check if the row has a disabled property or if it's an inherited property
    return (
      rowData.disabled === true ||
      rowData.id?.toString().startsWith('inherited_')
    );
  }

  hasDisabledRows(): boolean {
    return this.dataSource.some((row) => this.isRowDisabled(row));
  }

  getTranslatedOptions(
    options: { label: string; value: any }[],
  ): { label: string; value: any }[] {
    if (!options) return [];
    return options.map((option) => ({
      ...option,
      label: this.translateService.instant(option.label),
    }));
  }

  onCellValueChange(rowData: any, prop: string, value: any): void {
    // Update the row data
    rowData[prop] = value;

    // Emit the change event for parent components to handle
    this.cellValueChange.emit({
      rowData,
      prop,
      value,
    });
  }

  onInputChange(event: Event, rowData: any, prop: string): void {
    const value = (event.target as HTMLInputElement).value;
    // Update the row data immediately for display
    rowData[prop] = value;
  }

  onBlurChange(rowData: any, prop: string): void {
    const value = rowData[prop];
    // Emit the change event when user leaves the field
    this.onCellValueChange(rowData, prop, value);
  }

  onInputNumberChange(value: any, rowData: any, prop: string): void {
    // This is called when ngModel changes
    // Update the row data with the new value
    rowData[prop] = value;

    // Emit live input change for realtime validation use-cases
    this.cellInputChange.emit({
      rowData,
      prop,
      value,
    });
  }

  onInputNumberBlur(event: any, rowData: any, prop: string): void {
    // Get the current value from the PrimeNG component
    // The component should have already updated the ngModel value
    const currentValue = rowData[prop];

    // Convert to number if it's a valid number, otherwise keep as null
    let numericValue = null;
    if (
      currentValue !== null &&
      currentValue !== undefined &&
      currentValue !== ''
    ) {
      const parsedValue = Number(currentValue);
      if (!Number.isNaN(parsedValue)) {
        numericValue = parsedValue;
      }
    }

    // Update rowData with the correct numeric value
    rowData[prop] = numericValue;

    // Emit the change event with the correct value
    this.onCellValueChange(rowData, prop, numericValue);
  }

  onInputCellClick(
    event: Event,
    rowIndex: number,
    prop: string,
    inputType: 'inputText' | 'inputNumber',
  ): void {
    // Stop event propagation to prevent table row selection and blur events
    event.stopPropagation();

    // Check if the click was directly on the input element
    const target = event.target as HTMLElement;
    const isDirectInputClick =
      target.tagName === 'INPUT' || target.closest('input');

    // Only prevent default if clicking on the wrapper, not the input itself
    // This allows the input to handle its own click behavior naturally
    if (!isDirectInputClick) {
      event.preventDefault();

      // Focus the input field when clicking on the wrapper div
      const inputId = `input-${rowIndex}-${prop}`;
      const inputElement = document.getElementById(inputId) as HTMLInputElement;

      if (inputElement) {
        // Use requestAnimationFrame to ensure focus happens after current event cycle
        requestAnimationFrame(() => {
          inputElement.focus();
          // Let the browser handle cursor positioning naturally on click
        });
      } else if (inputType === 'inputNumber') {
        // For PrimeNG inputNumber, find the input inside the component
        const inputNumberComponent = (event.target as HTMLElement).closest(
          'p-inputnumber',
        );
        if (inputNumberComponent) {
          requestAnimationFrame(() => {
            const input = inputNumberComponent.querySelector(
              'input',
            ) as HTMLInputElement;
            if (input) {
              input.focus();
            }
          });
        }
      }
    }
    // If clicking directly on input, let it handle focus and cursor positioning naturally
  }

  private updateSelectAllState(): void {
    // Update the select all checkbox state based on current selection
    const selectableRows = this.dataSource.filter(
      (row) => !this.isRowDisabled(row),
    );

    if (selectableRows.length === 0) {
      this.isAllSelected = false;
    } else {
      const selectedSelectableRows = selectableRows.filter((row) =>
        this.selection.some((selectedItem) => {
          // Try ID comparison first if both have IDs
          if (row?.id && selectedItem?.id) {
            return (
              row.id === selectedItem.id ||
              row.id.toString() === selectedItem.id.toString()
            );
          }
          // Fallback to object reference comparison
          return row === selectedItem;
        }),
      );

      this.isAllSelected =
        selectedSelectableRows.length === selectableRows.length &&
        selectableRows.length > 0;
    }
  }

  // Get max value for input number components
  getMaxValue(col: TableColumn, rowData: any): number {
    if (typeof col.max === 'number') {
      return col.max;
    } else if (typeof col.max === 'string') {
      // Dynamic max value from rowData property
      return rowData[col.max] || 0;
    }
    return 0;
  }

  // Get disabled state for column
  getColumnDisabled(col: TableColumn, rowData: any): boolean {
    if (typeof col.disabled === 'boolean') {
      return col.disabled;
    } else if (typeof col.disabled === 'string') {
      // Dynamic disabled state from rowData property
      return rowData[col.disabled] || false;
    }

    // Special handling for received quantity column - disable if item is fully received
    if (col.prop === 'receivedQty' && rowData.isFullyReceived) {
      return true;
    }

    return false;
  }

  // Format date to display only date part (YYYY-MM-DD)
  formatDate(dateValue: any): string {
    if (!dateValue) {
      return '';
    }

    try {
      const date = new Date(dateValue);
      if (Number.isNaN(date.getTime())) {
        return '';
      }

      // Format as YYYY-MM-DD
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  }

  // Format currency using the number-formatter utility
  formatCurrency(value: any): string {
    return formatCurrency(value);
  }

  // Format number using the number-formatter utility (no currency symbol)
  formatNumber(value: any): string {
    return formatNumber(value);
  }
}
